import '../work-order.scss';
import { Modal } from '../../../components/modal/modal';
import { useEffect, useState } from 'react';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import { Spinner } from '../../../components/spinner/spinner';
import { FieldCounter, FieldLabel } from '../../../components/field/field';
import { WorkOrderProduct, WorkOrderProductInput } from '../../../clients/quipli-service/work-order/work-order';
import { OrderType } from '../../../clients/quipli-service/order/order';
import { Location } from '../../../clients/quipli-service/location/location';
import { CalculateSalePriceResult } from '../../../clients/quipli-service/pricing/pricing';
import { PriceOption, SearchProductPrice } from '../../order/add-product/search-product-price';
import { ProductType } from '../../../clients/quipli-service/product/product';
import { TextArea } from '../../../components/textarea/textarea';
import { Radio } from '../../../components/radio/radio';

export const WorkOrderPartsForm = ({
  isOpen,
  location,
  setIsOpen,
  onAddPart,
  onEditPart,
  workOrderProduct,
}: {
  isOpen: boolean;
  location: Location;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onAddPart: (input: WorkOrderProductInput) => void;
  onEditPart: (input: WorkOrderProductInput) => void;
  workOrderProduct?: WorkOrderProduct;
}) => {
  const api = useAPI();

  // React states
  const [isRequestInProgress, setRequestInProgress] = useState<boolean>(false);
  const [quantity, setQuantity] = useState<number>(workOrderProduct?.quantity || 1);
  const [priceOption, setPriceOption] = useState<PriceOption | null>(null);
  const [calculatedSalePrice, setCalculatedSalePrice] = useState<number>();
  const [productType, setProductType] = useState<ProductType>(ProductType.Part);

  // Callbacks
  const submitPart = async () => {
    if (workOrderProduct) {
      if (!quantity) return;

      // Block buttons and exit of modal
      setRequestInProgress(true);

      const partInput = {
        id: workOrderProduct.id,
        productId: workOrderProduct.product.id,
        customerUnitPrice: workOrderProduct.customerUnitPrice,
        quantity: quantity,
      };

      onEditPart(partInput);

      setRequestInProgress(false);
    } else {
      if (!priceOption || !quantity) return;

      // Block buttons and exit of modal
      setRequestInProgress(true);

      const partInput = {
        productId: priceOption.object.productId!,
        customerUnitPrice: calculatedSalePrice,
        quantity: quantity,
      };

      onAddPart(partInput);

      setRequestInProgress(false);
    }

    // Close modal
    setIsOpen(false);
    clear();
  };

  const clear = () => {
    setProductType(ProductType.Part);
    setQuantity(1);
    setPriceOption(null);
    setCalculatedSalePrice(undefined);
  };

  const handleOnChangePriceOption = (priceOption: PriceOption | null) => {
    if (!location) return;

    setPriceOption(priceOption);

    // Option removed
    if (!priceOption) {
      clear();
      return;
    }

    // Block buttons and exit of modal
    setRequestInProgress(true);

    // Calculate sale price
    if (priceOption.object.type === OrderType.Sale) {
      api
        .calculatePriceAsAdmin({
          productId: priceOption.object.productId,
          type: OrderType.Sale,
          locationId: location?.id,
        })
        .then((res) => {
          setCalculatedSalePrice((res as CalculateSalePriceResult).totalPrice / 100);
        });
    }

    setRequestInProgress(false);
  };

  const isAddButtonDisabled = (): boolean => {
    return isRequestInProgress || (!workOrderProduct && priceOption === null) || quantity <= 0;
  };

  const closeModal = () => {
    // Don't close modal if there is a request in progress
    if (isRequestInProgress) return;

    clear();
    setIsOpen(false);
  };

  useEffect(() => {
    if (!workOrderProduct) return;

    setProductType(workOrderProduct.product.type);
    setQuantity(workOrderProduct.quantity);
  }, [workOrderProduct]);

  return (
    <Modal
      isOpen={isOpen}
      title={
        workOrderProduct
          ? workOrderProduct.product.type === 'Part'
            ? 'Edit Part'
            : 'Edit Service'
          : 'Add Part or Service'
      }
      actions={
        isRequestInProgress ? (
          <Spinner />
        ) : (
          <>
            <button
              className="button button-small button-block button-secondary"
              disabled={isRequestInProgress}
              onClick={() => closeModal()}
            >
              Cancel
            </button>

            <button
              className="button button-small button-block button-primary"
              disabled={isAddButtonDisabled()}
              onClick={() => submitPart()}
            >
              {workOrderProduct ? 'Update' : 'Add'}
            </button>
          </>
        )
      }
      close={closeModal}
    >
      <div>
        {/* Part or Service */}
        {!workOrderProduct && (
          <div className="flex gap-5 my-2">
            <div className="flex gap-1 items-center">
              <Radio isChecked={productType === ProductType.Part} onChange={() => setProductType(ProductType.Part)} />
              <FieldLabel label="Part" />
            </div>
            <div className="flex gap-1 items-center">
              <Radio
                isChecked={productType === ProductType.Service}
                onChange={() => setProductType(ProductType.Service)}
              />
              <FieldLabel label="Service" />
            </div>
          </div>
        )}

        {/* Part */}
        <div className="flex flex-col gap-[4px]">
          <FieldLabel label={productType === ProductType.Part ? 'Part' : 'Service'} isRequired />
          {workOrderProduct ? (
            <TextArea
              className="w-full h-full resize-none text-sm"
              value={workOrderProduct.product.name}
              disabled={true}
            />
          ) : (
            <SearchProductPrice
              location={location}
              type={OrderType.Sale}
              value={priceOption || undefined}
              onSelect={handleOnChangePriceOption}
              productType={productType}
              placeholder={productType === ProductType.Part ? 'Search parts' : 'Search services'}
            />
          )}
        </div>

        {/* Quantity */}
        <div className="work-order-field mt-4">
          <FieldLabel label="Quantity" isRequired />

          <FieldCounter
            value={quantity}
            onChange={setQuantity}
            min={0}
            allowDecimals={productType === ProductType.Service}
          />
        </div>
      </div>
    </Modal>
  );
};
